<template>
	<div>
		<CContextMenu
			:entity-id="entityId"
			:entity-type="entityType"
			:entity-state="entityState"
			:entity-links="links"
			:position="position"
			:open-method="openMethod"
			:open-location="openLocation"
			:custom-class="customClass"
			@create-person-form="dialogState.createPersonForm = true"
			@new-form="handleCreateForm"
			@permissions-folder="dialogState.permissions = true"
			@rename-folder="renameFolder"
			@archive-folder="dialogState.archiveFolder = true"
			@delete-folder="deleteFolder"
			@restore-folder="restoreFolder"
			@permissions-form="dialogState.permissions = true"
			@import-entries="dialogState.importEntries = true"
			@form-details="dialogState.formDetails = true"
			@pin-form="pinForm"
			@copy-form="dialogState.copyForm = true"
			@move-to-folder="dialogState.moveToFolder = true"
			@archive-form="archiveForm"
			@unarchive-form="unarchiveForm"
			@delete-form="dialogState.deleteForm = true"
			@rename-view="renameView"
			@duplicate-view="duplicateView"
			@delete-view="deleteView"
			@unpin="unpin"
			@rename-pin="renamePin"
			@view-settings="dialogState.viewSettings = true"
			@visible-change="isVisible = $event"
			@sync-view-to="openSyncDialog"
			@org-audit-log="dialogState.organizationAuditLog = true"
		/>
		<template v-if="isFolder">
			<CArchiveFolderDialog
				v-if="dialogState.archiveFolder"
				show
				:folder-name="folderMeta.Name"
				@confirm-archive="archiveFolder"
				@closed="dialogState.archiveFolder = false"
			/>
			<CCreatePersonFormContainer
				v-if="dialogState.createPersonForm"
				show
				:org-code="orgCode"
				:folder-id="entityId"
				@closed="dialogState.createPersonForm = false"
			/>
			<CCreateFormContainer
				v-if="dialogState.createForm"
				show
				:folder-id="entityId"
				@closed="onCreateFormClosed"
			/>
		</template>
		<template v-if="isForm">
			<CFormDetailsDialog
				v-if="dialogState.formDetails"
				:form-id="entityId"
				show
				@org-audit-log="dialogState.organizationAuditLog = true"
				@closed="dialogState.formDetails = false"
			/>
			<CImportEntriesDialog
				v-if="dialogState.importEntries"
				show
				:form-display-name="formMeta.Name"
				:is-org-owner="isOrgOwner"
				:org-code="orgCode"
				:is-current-form="isCurrentForm"
				:form-id="formMeta.Id"
				@close="dialogState.importEntries = false"
				@closed="dialogState.importEntries = false"
			/>
			<CCopyFormDialog
				v-if="dialogState.copyForm"
				show
				track-change
				:form-id="entityId"
				:is-current-form="isCurrentForm"
				@closed="dialogState.copyForm = false"
			/>
			<CDeleteFormsDialog
				v-if="dialogState.deleteForm"
				show
				allow-use-form-name
				:form-ids="[entityId]"
				@closed="dialogState.deleteForm = false"
			/>
			<CMoveFormsToFolderContainer
				v-if="dialogState.moveToFolder"
				is-standalone
				:form-ids="[entityId]"
				@visible-change="dialogState.moveToFolder = false"
			/>
		</template>

		<template v-if="isView">
			<CCopyEntryViewDialog
				v-if="dialogState.copyView"
				show
				:view="viewMeta"
				@copy="copyView"
				@closed="dialogState.copyView = false"
			/>
			<CViewSettingsDialogContainer
				v-if="dialogState.viewSettings"
				show
				:view-id="viewMeta.Id"
				:entry-views="formMeta.EntryViews"
				:is-org-owner="isOrgOwner"
				:show-tasks="showTasks"
				:org-code="orgCode"
				@closed="dialogState.viewSettings = false"
			/>
			<CEntryViewLimitExceededDialog
				v-if="dialogState.entryLimitExceeded"
				show
				:limit="viewLimit"
				@close="dialogState.entryLimitExceeded = false"
			/>
			<CViewSettingsDialogContainer
				v-if="dialogState.renameView"
				show
				:view-id="viewMeta.Id"
				:entry-views="formMeta.EntryViews"
				:is-org-owner="isOrgOwner"
				:org-code="orgCode"
				rename-view
				@closed="dialogState.renameView = false"
			/>
			<CEntryViewSyncDialog
				v-if="dialogState.syncView"
				show
				:view-id="viewMeta.Id"
				:sync-service="syncService"
				@closed="dialogState.syncView = false"
			/>
		</template>

		<template v-if="isFolder || isForm">
			<CPermissionOverrideContainer
				v-if="dialogState.permissions"
				show
				:entity-id="entityId"
				:is-folder="isFolder"
				:name="isForm ? formMeta.Name : folderMeta.Name"
				:org-code="orgCode"
				:is-org-owner="isOrgOwner"
				:can-change-folder-permissions="canChangeFolderPermissions"
				@closed="dialogState.permissions = false"
			/>
			<OrganizationAuditLogDialog
				v-if="dialogState.organizationAuditLog"
				show
				:entity-id="entityId"
				:entity-type="isForm ? 'Form' : 'Folder'"
				:org-code="orgCode"
				:is-org-owner="isOrgOwner"
				@closed="dialogState.organizationAuditLog = false"
			/>
		</template>
	</div>
</template>

<script lang="ts">
	// See context-menu-defaults.ts for actions and their event names

	import { computed, defineComponent, inject, reactive, ref, watch, onMounted, onUnmounted, getCurrentInstance } from 'vue';
	import { globalStore } from 'src/stores/global-store';
	import CContextMenu from './ContextMenu.vue';
	import CImportEntriesDialog from 'src/components/dialogs/ImportEntriesDialog.vue';
	import CFormDetailsDialog from 'src/components/dialogs/FormDetailsDialog.vue';
	import CCopyFormDialog from 'src/components/dialogs/CopyFormDialog.vue';
	import CMoveFormsToFolderContainer from './MoveFormsToFolderContainer.vue';
	import CDeleteFormsDialog from 'src/components/dialogs/DeleteFormsDialog.vue';
	import CPermissionOverrideContainer from 'src/components/dialogs/PermissionOverrideContainer.vue';
	import CArchiveFolderDialog from 'src/components/dialogs/ArchiveFolderDialog.vue';
	import CCopyEntryViewDialog from 'src/components/dialogs/CopyEntryViewDialog.vue';
	import CEntryViewLimitExceededDialog from 'src/components/dialogs/EntryViewLimitExceededDialog.vue';
	import CViewSettingsDialogContainer from './ViewSettingsDialogContainer.vue';
	import CEntryViewSyncDialog from 'src/views/entries/EntryViewSyncDialog.vue';
	import CCreatePersonFormContainer from 'src/components/dialogs/CreatePersonFormContainer.vue';
	import CCreateFormContainer from 'src/components/navigation/new-form/CreateFormContainer.vue';
	import OrganizationAuditLogDialog from 'src/components/dialogs/OrganizationAuditLogDialog.vue';
	import notify from 'src/utilities/notify';
	import cognitoMessenger from 'src/utilities/cognito-messaging';
	import { useRoute } from 'vue-router/composables';
	import { checkRole } from 'src/views/admin/utils/role-check';
	import { useContextMenuState } from './use-context-menu';
	import { createPinNotification } from 'src/composables/use-create-pin-notification';
	import { navStateKey } from 'src/utilities/injection-keys';
	import { UserPinType } from '@cognitoforms/types/server-types/model/user-pin-type';
	import { ViewType } from '@cognitoforms/types/server-types/forms/model/view-type';
	import { ClientRole } from '@cognitoforms/types/server-types/core/services/client-role';
	import type { UserEntryViewMeta } from '@cognitoforms/types/server-types/core/services/user-entry-view-meta';
	import type { ContextMenuLinks, ContextMenuEntityState } from './context-menu-defaults';
	import type { IContextMenuState } from './use-context-menu';

	const dialogType = [
		'archiveFolder',
		'formDetails',
		'copyForm',
		'moveToFolder',
		'deleteForm',
		'importEntries',
		'permissions',
		'copyView',
		'viewSettings',
		'entryLimitExceeded',
		'renameView',
		'syncView',
		'createPersonForm',
		'createForm',
		'organizationAuditLog'
	] as const;

	type DialogType = typeof dialogType[number];

	export default defineComponent({
		name: 'CContextMenuContainer',
		components: {
			CContextMenu,
			CFormDetailsDialog,
			CCopyFormDialog,
			CDeleteFormsDialog,
			CImportEntriesDialog,
			CArchiveFolderDialog,
			CPermissionOverrideContainer,
			CCopyEntryViewDialog,
			CEntryViewLimitExceededDialog,
			CViewSettingsDialogContainer,
			CEntryViewSyncDialog,
			CMoveFormsToFolderContainer,
			CCreatePersonFormContainer,
			CCreateFormContainer,
			OrganizationAuditLogDialog
		},
		setup() {
			const ctxMenuState: IContextMenuState = useContextMenuState();
			const navState = inject(navStateKey);
			const route = useRoute();

			const dialogState = reactive<Record<DialogType, boolean>>({
				archiveFolder: false,
				formDetails: false,
				copyForm: false,
				deleteForm: false,
				importEntries: false,
				permissions: false,
				copyView: false,
				viewSettings: false,
				entryLimitExceeded: false,
				renameView: false,
				syncView: false,
				moveToFolder: false,
				createPersonForm: false,
				createForm: false,
				organizationAuditLog: false
			});

			const orgCode = computed(() => globalStore.organization.Code);
			const preservedCaseOrgCode = computed(() => globalStore.organization.PreservedCaseCode);
			const isOrgOwner = computed(() => checkRole(ClientRole.Owner));
			let dialogRestoreCallback = null;

			const customClass = computed(() => `global-context-menu ${ctxMenuState.blockInteraction ? 'flyout-menu__context-menu--block-interaction' : ''}`);

			// Folders
			const isFolder = computed(() => ctxMenuState.entityType === 'folder');
			const folderMeta = computed(() => isFolder.value ? globalStore.folders[ctxMenuState.entityId] : null);
			const canChangeFolderPermissions = computed(() => globalStore.organization.Plan.AllowFolderPermissions);

			const renameFolder = () => {
				navState.setNodeRenameState(ctxMenuState.entityId, ctxMenuState.treeMode);
			};

			const archiveFolder = () => {
				globalStore.archiveFolder(ctxMenuState.entityId).catch(error => {
					if (error.statusCode === 403)
						notify('You must be an administrator of every form in this folder to archive.');
				});
			};

			const deleteFolder = () => {
				globalStore.deleteFolder(ctxMenuState.entityId).catch(error => {
					if (error.statusCode === 403)
						notify('This folder is not empty and cannot be deleted.');
				});
			};

			const restoreFolder = () => {
				globalStore.restoreFolder(ctxMenuState.entityId).catch(error => {
					if (error.statusCode === 403)
						notify('You must be an administrator of every form in this folder to restore.');
				});
			};

			const isForm = computed(() => ctxMenuState.entityType === 'form');
			const isView = computed(() => ctxMenuState.entityType === 'view');

			// Forms
			const formMeta = computed(() => {
				if (isForm.value)
					return globalStore.forms[ctxMenuState.entityId];
				else if (isView.value)
					return globalStore.forms[ctxMenuState.entityId.split('-')[0]];
				else
					return null;
			});
			const isCurrentForm = computed(() => formMeta.value.Id === globalStore.currentForm?.Id);

			const archiveForm = () => globalStore.archiveForms([ctxMenuState.entityId]);
			const unarchiveForm = () => globalStore.restoreForms([ctxMenuState.entityId]);

			const createElement = ref(null);
			onMounted(() => { createElement.value = getCurrentInstance().proxy.$createElement; });

			const userPins = globalStore.userPins.reduce((dictionaryItem, pin) => {
				dictionaryItem[pin.Id] = pin;
				return dictionaryItem;
			}, {});

			const pinForm = () => {
				const formName = formMeta.value.Name;

				globalStore.pinItem(ctxMenuState.entityId, null, UserPinType.Form)
					.then(() =>
						createPinNotification(formName, ctxMenuState.entityId, createElement.value)
					).catch(error => {
						if (error.message)
							notify(error.message, 'error');
					});
			};

			const awaitingCheckIfSaved = ref(false);
			const checkIfSaved = (callback) => {
				// Call Cognito.navigate if it is on the window to prompt unsaved changes warning
				if (window.Cognito?.navigate) {
					awaitingCheckIfSaved.value = true;
					window.Cognito.navigate(true).then((restoreCallback) => {
						awaitingCheckIfSaved.value = false;
						dialogRestoreCallback = restoreCallback;
						callback();
					});
				}
				else
					callback();
			};

			const restoreChangesIfDiscarded = () => {
				if (dialogRestoreCallback) {
					dialogRestoreCallback();
					dialogRestoreCallback = null;
				}
			};

			const handleCreateForm = () => {
				checkIfSaved(() => { dialogState.createForm = true; });
			};

			const onCreateFormClosed = () => {
				dialogState.createForm = false;
				restoreChangesIfDiscarded();
			};

			// Views
			const formId = computed(() => ctxMenuState.entityId.split('-')[0]);
			const viewMeta = computed<UserEntryViewMeta>(() => {
				if (!isView.value)
					return null;

				return globalStore.forms[formId.value].EntryViews.find(v => v.Id === ctxMenuState.entityId);
			});

			const viewLimit = computed(() => globalStore.organization.Plan.EntryViewLimit);
			const atViewLimit = computed(() => globalStore.forms[formId.value] ? globalStore.forms[formId.value].EntryViews.length >= viewLimit.value : false);

			const renameView = () => {
				if (ctxMenuState.openLocation === 'viewTabs')
					dialogState.renameView = true;
				else
					navState.setNodeRenameState(ctxMenuState.entityId, ctxMenuState.treeMode);
			};

			const duplicateView = () => {
				if (atViewLimit.value)
					dialogState.entryLimitExceeded = true;
				else
					dialogState.copyView = true;
			};
			const copyView = (newName: string) => globalStore.copyView(ctxMenuState.entityId, newName);
			const deleteView = () => {
				// If on entries page for this form, use entries.js deleteView to handle unsaved entry changes
				if (ctxMenuState.openLocation === 'viewTabs' || (isCurrentForm.value && route.name === 'entries'))
					cognitoMessenger.trigger('deleteView', { data: ctxMenuState.entityId });
				// Otherwise, use globalStore
				else
					globalStore.deleteView(ctxMenuState.entityId);

				// On deletion of a view, strip off the view portion of the ID to focus the form
				(document.querySelector(`[id^=node-${ctxMenuState.entityId.split('-')[0]}-default]`) as HTMLElement).focus();
			};
			const syncService = ref(null);
			const openSyncDialog = (service) => {
				syncService.value = service;
				dialogState.syncView = true;
			};

			const showTasks = computed(()=> globalStore.organization.Plan.AllowWorkflowTasks);

			// Pinned Entities
			const unpin = () => globalStore.unpinItem(ctxMenuState.entityId);
			const renamePin = () => navState.setNodeRenameState(ctxMenuState.entityId, ctxMenuState.treeMode);

			// Context Menu Entity State
			const canMoveFormToFolder = (form) => {
				// If cannot manage form or have folders, return early
				if (!form.CanManageForm || !globalStore.organization.Plan.AllowCreateFolders)
					return false;

				// Global admin and has folders
				if (checkRole(ClientRole.Administrator) && Object.values(globalStore.folders).length > 0)
					return true;

				// Can manage at least 1 folder other than the forms current folder
				const foldersCanManage = Object.values(globalStore.folders).filter(f => f.CanManageFolder && f.Id !== form.FolderId);
				if (foldersCanManage.length > 0)
					return true;

				return false;
			};

			const canCopyForm = (form) => {
				// If cannot manage form, return early
				if (!form.CanManageForm)
					return false;

				// Global admin
				if (checkRole(ClientRole.Administrator))
					return true;

				// Can manage at least one folder
				if (Object.values(globalStore.folders).filter(f => f.CanManageFolder).length > 0)
					return true;

				return false;
			};

			const entityState = computed<ContextMenuEntityState>(() => ({
				allowCreateFolders: globalStore.organization.Plan.AllowCreateFolders,
				...(isFolder.value ?
					{
						isArchived: folderMeta.value.IsArchived || ctxMenuState.treeMode === 'archived',
						canManageFolder: folderMeta.value.CanManageFolder,
						hasChildren: Object.values(globalStore.forms).filter(f => f.FolderId === ctxMenuState.entityId).length !== 0
					} : {}),
				...(isForm.value ?
					{
						isArchived: formMeta.value.IsArchived,
						canManageForm: formMeta.value.CanManageForm,
						canManageEntries: formMeta.value.CanManageEntries,
						isPinTree: ctxMenuState.treeMode === 'pinned',
						canMoveToFolder: canMoveFormToFolder(formMeta.value),
						canPin: !(ctxMenuState.entityId in userPins) && globalStore.organization.Role !== ClientRole.Guest,
						canCopy: canCopyForm(formMeta.value)
					} : {}),
				...(isView.value ?
					{
						canDelete: viewMeta.value.Type === ViewType.Form || formMeta.value.EntryViews.filter(v => v.Type === ViewType.Table).length !== 1,
						canManageEntries: formMeta.value.CanManageEntries,
						canManageForm: formMeta.value.CanManageForm,
						allowEntryViews: globalStore.organization.Plan.AllowEntryViews,
						canSync: !viewMeta.value.IsTaskView && viewMeta.value.Type === ViewType.Table && !viewMeta.value.IsUserSpecific
					} : {})
			}));

			const links = computed<ContextMenuLinks>(() => {
				if (isForm.value)
					return {
						ViewEntriesInNewTab: `/${orgCode.value}/${formMeta.value.UrlName}/entries`,
						EditForm: { name: 'builder', params: { orgCode: orgCode.value, formUrlName: formMeta.value.UrlName } },
						EditFormOrgSettings: { name: 'builder', params: { orgCode: orgCode.value, formUrlName: formMeta.value.UrlName } },
						PublishForm: { name: 'publish', params: { orgCode: orgCode.value, formUrlName: formMeta.value.UrlName } },
						PublishFormOrgSettings: { name: 'publish', params: { orgCode: orgCode.value, formUrlName: formMeta.value.UrlName } },
						PublicLink: `/${preservedCaseOrgCode.value}/${formMeta.value.PreservedCaseUrlName}`
					};
				else if (isView.value && viewMeta.value)
					return {
						ViewEntryViewInNewTab: `/${orgCode.value}/${formMeta.value.UrlName}/${ctxMenuState.entityId.split('-')[1]}-${viewMeta.value.InternalName}`
					};
				else
					return {};
			});

			// Updates context menu state and is interacting state when dialogState or isVisible changes
			const isVisible = ref(false);
			const updateInteractionState = () => {
				ctxMenuState.dialogOpened = Object.values(dialogState).some(Boolean) || awaitingCheckIfSaved.value;
				if (isVisible.value)
					navState.setIsHandlingInteraction(true);
				else {
					navState.setIsHandlingInteraction(false);
					if (!ctxMenuState.dialogOpened)
						ctxMenuState.isOpen = false;
				}
			};

			watch(dialogState, updateInteractionState, { deep: true });
			watch(isVisible, (visible) => {
				if (visible && awaitingCheckIfSaved.value)
					awaitingCheckIfSaved.value = false;
				updateInteractionState();
			});

			// Ensure interaction state is updated when component is unmounted
			onUnmounted(() => {
				if (isVisible.value)
					navState.setIsHandlingInteraction(false);
			});

			return {
				...ctxMenuState,
				entityState,
				isVisible,
				links,
				dialogState,
				orgCode,
				isOrgOwner,
				customClass,

				// Folder
				isFolder,
				folderMeta,
				canChangeFolderPermissions,
				renameFolder,
				archiveFolder,
				deleteFolder,
				restoreFolder,

				// Forms
				isForm,
				formMeta,
				archiveForm,
				unarchiveForm,
				pinForm,
				isCurrentForm,
				handleCreateForm,
				onCreateFormClosed,

				// Views
				isView,
				showTasks,
				viewMeta,
				viewLimit,
				atViewLimit,
				renameView,
				duplicateView,
				copyView,
				deleteView,
				openSyncDialog,
				syncService,

				// Pin
				unpin,
				renamePin
			};
		}
	});
</script>
